import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getApiUrl } from '../../config/api';
import './MassSMSHistory.css';

const fmt = (dt) => {
  if (!dt) return '—';
  return new Date(dt).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true
  });
};

const fmtShort = (dt) => {
  if (!dt) return '—';
  return new Date(dt).toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true
  });
};

const R2_URL = /https:\/\/media\.dinitracker\.com\/\S+/;

function parseMessage(msg) {
  if (!msg) return { text: '', imageUrl: null };
  const match = msg.match(R2_URL);
  if (!match) return { text: msg.trim(), imageUrl: null };
  const imageUrl = match[0];
  const text = msg.replace(imageUrl, '').replace(/\n+$/, '').trim();
  return { text, imageUrl };
}

const STATUS_DOT = {
  completed: 'green',
  failed: 'red',
  cancelled: 'orange',
  running: 'blue',
  queued: 'gray',
};

function MetaNumber({ value, label, color = 'default' }) {
  return (
    <div className="sms-meta-num">
      <span className={`sms-meta-val sms-meta-val--${color}`}>
        {(value ?? 0).toLocaleString()}
      </span>
      <span className="sms-meta-lbl">{label}</span>
    </div>
  );
}

function CampaignDetail({ jobKey, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('responses');

  useEffect(() => {
    axios.get(getApiUrl(`/api/customers/mass-sms/${jobKey}/detail`))
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [jobKey]);

  if (loading) return (
    <div className="sms-state-center">
      <div className="sms-spinner" />
      <p>Loading campaign</p>
    </div>
  );
  if (!data) return (
    <div className="sms-state-center">
      <p className="sms-state-error">Failed to load campaign data.</p>
    </div>
  );

  const { job, engagements, optOuts, failed } = data;
  const { text: jobText, imageUrl: jobImage } = parseMessage(job.Message);
  const deliveryRate = job.TotalRecipients > 0
    ? Math.round((job.Sent / job.TotalRecipients) * 100)
    : 0;
  const dotColor = STATUS_DOT[job.Status] || 'gray';

  return (
    <div className="sms-detail">
      <button className="sms-back-btn" onClick={onBack}>
        <i className="fas fa-arrow-left" /> All campaigns
      </button>

      <div className="sms-detail-header">
        <div className="sms-detail-eyebrow">
          <span className={`sms-dot sms-dot--${dotColor}`} />
          <span className="sms-detail-status">{job.Status}</span>
          {job.AudienceSummary && (
            <>
              <span className="sms-eyebrow-sep">·</span>
              <span className="sms-detail-audience">
                <i className="fas fa-users" /> {job.AudienceSummary}
              </span>
            </>
          )}
          <span className="sms-eyebrow-sep">·</span>
          <span className="sms-detail-byline">{job.StartedBy || 'Unknown'}, {fmt(job.StartedAt)}</span>
        </div>

        {jobText && <p className="sms-detail-message">{jobText}</p>}
        {jobImage && (
          <div className="sms-detail-image">
            <img
              src={jobImage}
              alt="Campaign image"
              onClick={() => window.open(jobImage, '_blank')}
            />
          </div>
        )}

        <div className="sms-stats-strip">
          <MetaNumber value={job.Sent} label="sent" color="green" />
          <div className="sms-stats-divider" />
          <MetaNumber value={job.Failed} label="failed" color="red" />
          <div className="sms-stats-divider" />
          <MetaNumber value={job.Skipped} label="skipped" />
          <div className="sms-stats-divider" />
          <MetaNumber value={engagements.length} label="replies" color="blue" />
          <div className="sms-stats-divider" />
          <MetaNumber value={optOuts.length} label="opted out" color="orange" />
          <div className="sms-stats-divider" />
          <div className="sms-delivery-wrap">
            <span className={`sms-meta-val sms-meta-val--${deliveryRate >= 90 ? 'green' : deliveryRate >= 70 ? 'default' : 'red'}`}>
              {deliveryRate}%
            </span>
            <span className="sms-meta-lbl">delivery</span>
            <div className="sms-delivery-bar">
              <div className="sms-delivery-fill" style={{ width: `${deliveryRate}%` }} />
            </div>
          </div>
        </div>
      </div>

      <div className="sms-tabs">
        {[
          { key: 'responses', label: 'Replies', count: engagements.length },
          { key: 'optouts',   label: 'Opt-outs', count: optOuts.length },
          { key: 'failed',    label: 'Failed', count: failed.length },
        ].map(t => (
          <button
            key={t.key}
            className={`sms-tab${tab === t.key ? ' sms-tab--active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            <span className="sms-tab-count">{t.count}</span>
          </button>
        ))}
      </div>

      <div className="sms-table-wrap">
        {tab === 'responses' && (
          engagements.length === 0
            ? <div className="sms-state-center sms-state-center--sm">
                <i className="fas fa-comment-slash" />
                <p>No replies recorded for this campaign</p>
              </div>
            : <table className="sms-table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Phone</th>
                    <th>Message</th>
                    <th>Type</th>
                    <th>Received</th>
                  </tr>
                </thead>
                <tbody>
                  {engagements.map((e, i) => (
                    <tr key={i}>
                      <td className="sms-td-name">{e.CustomerFullName || '—'}</td>
                      <td className="sms-td-phone">{e.PhoneNumber}</td>
                      <td className="sms-td-msg">{e.ResponseMessage}</td>
                      <td>
                        {e.ResponseType === 'interested'
                          ? <span className="sms-badge sms-badge--interested">Interested</span>
                          : <span className="sms-badge sms-badge--neutral">Neutral</span>
                        }
                      </td>
                      <td className="sms-td-date">{fmtShort(e.ReceivedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
        )}

        {tab === 'optouts' && (
          optOuts.length === 0
            ? <div className="sms-state-center sms-state-center--sm">
                <i className="fas fa-check" />
                <p>No opt-outs following this campaign</p>
              </div>
            : <table className="sms-table">
                <thead>
                  <tr><th>Customer</th><th>Phone</th><th>Opted Out</th></tr>
                </thead>
                <tbody>
                  {optOuts.map((o, i) => (
                    <tr key={i}>
                      <td className="sms-td-name">{o.CustomerFullName || '—'}</td>
                      <td className="sms-td-phone">{o.PhoneNumber}</td>
                      <td className="sms-td-date">{fmt(o.OptedOutAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
        )}

        {tab === 'failed' && (
          failed.length === 0
            ? <div className="sms-state-center sms-state-center--sm">
                <i className="fas fa-check" />
                <p>All recipients reached</p>
              </div>
            : <table className="sms-table">
                <thead>
                  <tr><th>Customer</th><th>Phone</th></tr>
                </thead>
                <tbody>
                  {failed.map((f, i) => (
                    <tr key={i}>
                      <td className="sms-td-name">{f.CustomerFullName || '—'}</td>
                      <td className="sms-td-phone">{f.PhoneNumber}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
        )}
      </div>
    </div>
  );
}

export default function MassSMSHistory({ onClose }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJobKey, setSelectedJobKey] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(getApiUrl('/api/customers/mass-sms/history'))
      .then(r => setCampaigns(r.data.campaigns || []))
      .catch(() => setCampaigns([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="sms-backdrop" onClick={onClose}>
      <div className="sms-modal" onClick={e => e.stopPropagation()}>
        <div className="sms-modal-header">
          <div className="sms-modal-title">
            <i className="fas fa-paper-plane" />
            <span>Campaign History</span>
          </div>
          <button className="sms-modal-close" onClick={onClose} aria-label="Close">
            <i className="fas fa-times" />
          </button>
        </div>

        <div className="sms-modal-body">
          {selectedJobKey ? (
            <CampaignDetail jobKey={selectedJobKey} onBack={() => setSelectedJobKey(null)} />
          ) : (
            <>
              {loading && (
                <div className="sms-state-center">
                  <div className="sms-spinner" />
                  <p>Loading campaigns</p>
                </div>
              )}

              {!loading && campaigns.length === 0 && (
                <div className="sms-state-center">
                  <i className="fas fa-paper-plane" />
                  <p>No campaigns sent yet</p>
                  <span>Mass texts you send will appear here with full delivery stats.</span>
                </div>
              )}

              {!loading && campaigns.length > 0 && (
                <div className="sms-list">
                  {campaigns.map(c => {
                    const dotColor = STATUS_DOT[c.Status] || 'gray';
                    const { text: rowText, imageUrl: rowImage } = parseMessage(c.Message);
                    return (
                      <div
                        key={c.JobKey}
                        className="sms-row"
                        onClick={() => setSelectedJobKey(c.JobKey)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => e.key === 'Enter' && setSelectedJobKey(c.JobKey)}
                      >
                        <span className={`sms-dot sms-dot--${dotColor} sms-dot--lg`} />

                        <div className="sms-row-content">
                          {rowImage && (
                            <img
                              className="sms-row-thumb"
                              src={rowImage}
                              alt="Campaign image"
                              onClick={e => { e.stopPropagation(); window.open(rowImage, '_blank'); }}
                            />
                          )}
                          <p className="sms-row-message">
                            {rowText.length > 100 ? rowText.slice(0, 100) + '…' : rowText}
                          </p>
                          <div className="sms-row-sub">
                            {c.AudienceSummary && (
                              <span className="sms-row-audience">
                                <i className="fas fa-users" /> {c.AudienceSummary}
                              </span>
                            )}
                            <span>{fmtShort(c.StartedAt)}</span>
                            <span>by {c.StartedBy || 'Unknown'}</span>
                          </div>
                        </div>

                        <div className="sms-row-nums">
                          <MetaNumber value={c.Sent} label="sent" color="green" />
                          <MetaNumber value={c.Failed} label="failed" color="red" />
                          <MetaNumber value={c.Responses} label="replies" color="blue" />
                          <MetaNumber value={c.OptOuts} label="opt-out" color="orange" />
                        </div>

                        <i className="fas fa-chevron-right sms-row-chevron" />
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
